import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import StaffCard from "../components/StaffCard";
import PerformanceBadge from "../components/PerformanceBadge";
import ProfilePhotoEditor from "../components/ProfilePhotoEditor";
import { FULL_MONTHS, thisMonth, thisYear, classNames } from "../lib/utils";
import { Link } from "react-router-dom";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip } from "recharts";
import { TrendingUp, MessageSquare, Sparkles } from "lucide-react";
import { toast } from "sonner";

const CHART_MARGIN = { top: 10, right: 10, left: -10, bottom: 0 };
const SCORE_DOMAIN = [0, 5];
const TOOLTIP_STYLE = { fontSize: 12, borderRadius: 8 };
const LINE_DOT_EMERALD = { r: 4, fill: "#059669" };
const LINE_DOT_SKY = { r: 4 };

export function StaffMyDashboard() {
  const { user } = useAuth();
  const [history, setHistory] = useState(null);
  const [year, setYear] = useState(thisYear());
  useEffect(() => { api.get("/me/history", { params: { year } }).then(({ data }) => setHistory(data)); },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [year]);
  if (!history) return <div className="p-8 text-slate-500">Loading…</div>;

  const current = history.rows.find((r) => r.month === thisMonth()) || history.rows[history.rows.length - 1];
  const cats = current ? [
    { key: "quality_average", label: "Quality of Care" },
    { key: "reliability_average", label: "Reliability" },
    { key: "documentation_average", label: "Documentation" },
    { key: "health_safety_average", label: "Health & Safety" },
    { key: "professionalism_average", label: "Professionalism" },
    { key: "compliance_average", label: "Compliance" },
  ].map((c) => ({ ...c, value: current[c.key] })) : [];
  const best = cats.length ? cats.reduce((a, b) => (a.value > b.value ? a : b)) : null;
  const weakest = cats.length ? cats.reduce((a, b) => (a.value < b.value ? a : b)) : null;
  const trend = history.rows.map((r) => ({ m: FULL_MONTHS[r.month - 1].slice(0, 3), score: r.overall_weighted_score }));

  return (
    <div className="p-6 sm:p-8 space-y-6">
      <div>
        <p className="text-small-caps">My Performance</p>
        <h1 className="text-h1 mt-1">My Dashboard</h1>
      </div>

      <StaffCard staff={{ ...user, home: user.assigned_home, role_title: user.role_title }} score={current?.overall_weighted_score} band={current?.performance_band} ytdAverage={history.yearly_average} />

      {!current ? (
        <div className="nv-card p-10 text-center text-slate-500">No scores recorded yet for {year}.</div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="nv-card p-5">
              <p className="text-small-caps">Current Month</p>
              <p className="mt-2 text-3xl font-heading font-bold text-slate-900 tabular-nums">{current.overall_weighted_score?.toFixed(2)}</p>
              <PerformanceBadge band={current.performance_band} className="mt-1" />
              <p className="text-xs text-slate-500 mt-2">{FULL_MONTHS[current.month - 1]} {current.year}</p>
            </div>
            <div className="nv-card p-5">
              <p className="text-small-caps">Year-to-Date Average</p>
              <p className="mt-2 text-3xl font-heading font-bold text-slate-900 tabular-nums">{history.yearly_average?.toFixed(2) || "0.00"}</p>
              <p className="text-xs text-slate-500 mt-2">{history.rows.length} months scored in {year}</p>
            </div>
            <div className="nv-card p-5">
              <p className="text-small-caps">Best Category</p>
              <p className="mt-2 text-lg font-heading font-bold text-emerald-700">{best?.label || "—"}</p>
              <p className="text-xs text-slate-500 mt-1">{best?.value?.toFixed(2) || 0}</p>
              <div className="mt-3 pt-3 border-t border-slate-100">
                <p className="text-small-caps">Weakest Category</p>
                <p className="mt-1 text-lg font-heading font-bold text-amber-700">{weakest?.label || "—"}</p>
                <p className="text-xs text-slate-500">{weakest?.value?.toFixed(2) || 0}</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="nv-card p-5 lg:col-span-2">
              <h3 className="text-h3 flex items-center gap-2"><TrendingUp size={16} /> Improvement Trend</h3>
              <div className="mt-4 h-56 min-w-0">
                <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0} debounce={50}>
                  <LineChart data={trend} margin={CHART_MARGIN}>
                    <XAxis dataKey="m" tickLine={false} axisLine={false} fontSize={12} />
                    <YAxis domain={SCORE_DOMAIN} tickLine={false} axisLine={false} fontSize={12} />
                    <Tooltip contentStyle={TOOLTIP_STYLE} />
                    <Line type="monotone" dataKey="score" stroke="#059669" strokeWidth={2.5} dot={LINE_DOT_EMERALD} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="nv-card p-5">
              <h3 className="text-h3 flex items-center gap-2"><MessageSquare size={16} /> Manager's Note</h3>
              <p className="mt-3 text-sm text-slate-700 whitespace-pre-wrap leading-relaxed min-h-[80px]">
                {current.manager_notes || <span className="text-slate-400 italic">No notes from your manager this period.</span>}
              </p>
              <div className="mt-4 pt-4 border-t border-slate-100">
                <p className="text-small-caps flex items-center gap-1.5"><Sparkles size={12} /> Recommendation</p>
                <p className="text-sm text-slate-700 mt-1.5 leading-relaxed">{current.recommendation}</p>
              </div>
            </div>
          </div>

          <div className="text-center">
            <Link to="/me/history" className="text-sm font-semibold text-sky-700 hover:underline">View full score history →</Link>
          </div>
        </>
      )}
    </div>
  );
}

export function StaffMyHistory() {
  const [year, setYear] = useState(thisYear());
  const [history, setHistory] = useState(null);
  const [view, setView] = useState("monthly");
  const [openMonth, setOpenMonth] = useState(null);

  useEffect(() => { api.get("/me/history", { params: { year } }).then(({ data }) => setHistory(data)); },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [year]);
  if (!history) return <div className="p-8 text-slate-500">Loading…</div>;

  const trend = history.rows.map((r) => ({ m: FULL_MONTHS[r.month - 1].slice(0, 3), score: r.overall_weighted_score }));

  return (
    <div className="p-6 sm:p-8 space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-small-caps">History</p>
          <h1 className="text-h1 mt-1">My Score History</h1>
        </div>
        <div className="flex gap-2">
          <input type="number" value={year} onChange={(e) => setYear(+e.target.value)} className="text-sm rounded-md border border-slate-200 bg-white px-3 py-2 w-24" />
          <div className="inline-flex p-1 bg-slate-100 rounded-md">
            <button onClick={() => setView("monthly")} className={classNames("px-3 py-1.5 text-xs font-semibold rounded", view === "monthly" ? "bg-white shadow-sm" : "text-slate-500")}>Monthly</button>
            <button onClick={() => setView("annual")} className={classNames("px-3 py-1.5 text-xs font-semibold rounded", view === "annual" ? "bg-white shadow-sm" : "text-slate-500")}>Annual</button>
          </div>
        </div>
      </div>

      {view === "annual" ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <KPI label="Yearly Average" value={history.yearly_average?.toFixed(2) || "0.00"} />
            <KPI label="Best Month" value={history.best_month ? `${FULL_MONTHS[history.best_month.month - 1]}` : "—"} sub={history.best_month?.overall_weighted_score?.toFixed(2)} />
            <KPI label="Lowest Month" value={history.lowest_month ? `${FULL_MONTHS[history.lowest_month.month - 1]}` : "—"} sub={history.lowest_month?.overall_weighted_score?.toFixed(2)} />
          </div>
          <div className="nv-card p-5">
            <h3 className="text-h3">Monthly Trend</h3>
            <div className="mt-4 h-64 min-w-0">
              <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0} debounce={50}>
                <LineChart data={trend} margin={CHART_MARGIN}>
                  <XAxis dataKey="m" tickLine={false} axisLine={false} fontSize={12} />
                  <YAxis domain={SCORE_DOMAIN} tickLine={false} axisLine={false} fontSize={12} />
                  <Tooltip contentStyle={TOOLTIP_STYLE} />
                  <Line type="monotone" dataKey="score" stroke="#0369a1" strokeWidth={2.5} dot={LINE_DOT_SKY} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      ) : (
        <div className="nv-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
              <tr><th className="px-5 py-3 text-left">Month</th><th className="px-3 py-3 text-right">Score</th><th className="px-3 py-3 text-left">Band</th><th className="px-3 py-3 text-left">Recommendation</th></tr>
            </thead>
            <tbody>
              {history.rows.map((r) => (
                <tr key={r.month} className="border-t border-slate-100 cursor-pointer hover:bg-slate-50/60" onClick={() => setOpenMonth(openMonth === r.month ? null : r.month)}>
                  <td className="px-5 py-3 font-medium text-slate-800">{FULL_MONTHS[r.month - 1]} {r.year}</td>
                  <td className="px-3 py-3 text-right font-semibold tabular-nums">{r.overall_weighted_score?.toFixed(2)}</td>
                  <td className="px-3 py-3"><PerformanceBadge band={r.performance_band} /></td>
                  <td className="px-3 py-3 text-slate-600 max-w-md truncate">{r.recommendation}</td>
                </tr>
              ))}
              {history.rows.length === 0 && <tr><td colSpan={4} className="px-5 py-10 text-center text-slate-500">No history for {year}.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function StaffMyProfile() {
  const { user, refresh } = useAuth();
  const [name, setName] = useState(user.full_name);
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { full_name: name };
      if (password) payload.password = password;
      await api.patch(`/users/${user.user_id}`, payload);
      await refresh();
      setPassword("");
      toast.success("Profile updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="p-6 sm:p-8 max-w-2xl space-y-6">
      <div>
        <p className="text-small-caps">Account</p>
        <h1 className="text-h1 mt-1">My Profile</h1>
      </div>
      <StaffCard staff={{ ...user, home: user.assigned_home, role_title: user.role_title }} />
      <ProfilePhotoEditor />
      <div className="nv-card p-5 space-y-4">
        <h3 className="text-h3">Update Profile</h3>
        <label className="block">
          <span className="text-small-caps">Full name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5 w-full text-sm rounded-md border border-slate-200 bg-white px-3 py-2" data-testid="profile-name-input" />
        </label>
        <label className="block">
          <span className="text-small-caps">Email</span>
          <input value={user.email} disabled className="mt-1.5 w-full text-sm rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-slate-500" />
        </label>
        <label className="block">
          <span className="text-small-caps">New password (leave blank to keep current)</span>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 w-full text-sm rounded-md border border-slate-200 bg-white px-3 py-2" data-testid="profile-password-input" />
        </label>
        <button onClick={save} disabled={saving} className="px-4 py-2 rounded-md text-sm font-semibold bg-sky-800 hover:bg-sky-900 text-white disabled:opacity-60" data-testid="profile-save-btn">
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </div>
  );
}

function KPI({ label, value, sub }) {
  return (
    <div className="nv-card p-5">
      <p className="text-small-caps">{label}</p>
      <p className="mt-2 text-3xl font-heading font-bold text-slate-900 tabular-nums">{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
    </div>
  );
}
